import { Input } from "antd";
import { Icon } from "@iconify/react";
import { cn } from "@/shared/lib/utils";

interface AuthFieldBaseProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoComplete?: string;
  error?: string;
  className?: string;
  /** Iconify icon name shown as a left prefix inside the input (e.g. "lucide:mail"). */
  icon?: string;
  disabled?: boolean;
}

// This field's exact look (rounded-xl = 16px, py-2.5 px-4, custom clear icon)
// diverges from the global Input theme (rounded-md = 6px, h-9), so it's
// styled locally via className rather than through shared Input tokens —
// forcing it through the global theme would change its radius/height.
export function AuthTextField({
  label,
  value,
  onChange,
  placeholder,
  autoComplete,
  error,
  className,
  icon,
  disabled,
}: AuthFieldBaseProps) {
  return (
    <div className={cn("mb-5", className)}>
      <label className="mb-2 block text-sm font-medium text-[#374151]">{label}</label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        status={error ? "error" : undefined}
        disabled={disabled}
        allowClear={!disabled}
        prefix={icon ? (
          <span className="mr-2.5 flex items-center gap-2.5">
            <Icon icon={icon} className="h-4 w-4 text-[#374151]" />
            <span className="h-5 w-px shrink-0 bg-[#DADDE5]" />
          </span>
        ) : undefined}
        className={cn(
          "!rounded-xl !border-[#E5E7EB] !px-4 !py-2.5 !text-sm !text-[#374151]",
          "[&.ant-input-status-error]:!border-[#E4002B]",
          "hover:!border-[#E4002B]/50 focus:!border-[#E4002B]/50 focus:!shadow-none",
        )}
      />
      {error && <p className="!mt-1 text-xs text-[#E4002B]">{error}</p>}
    </div>
  );
}

export function AuthPasswordField({
  label,
  value,
  onChange,
  placeholder,
  autoComplete,
  error,
  className,
  icon,
}: AuthFieldBaseProps) {
  return (
    <div className={cn("mb-5", className)}>
      <label className="mb-2 block text-sm font-medium text-[#374151]">{label}</label>
      <Input.Password
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        status={error ? "error" : undefined}
        prefix={icon ? (
          <span className="mr-2.5 flex items-center gap-2.5">
            <Icon icon={icon} className="h-4 w-4 text-[#374151]" />
            <span className="h-5 w-px shrink-0 bg-[#DADDE5]" />
          </span>
        ) : undefined}
        className={cn(
          "!rounded-xl !border-[#E5E7EB] !px-4 !py-2.5 !text-sm !text-[#374151]",
          "[&.ant-input-status-error]:!border-[#E4002B]",
          "hover:!border-[#E4002B]/50 focus:!border-[#E4002B]/50 focus:!shadow-none",
        )}
      />
      {error && <p className="!mt-1 text-xs text-[#E4002B]">{error}</p>}
    </div>
  );
}
